/**
 * @fileoverview AHA moment do onboarding: wizard guiado que coleta os 8 campos da
 * IA, chama POST /onboarding/ai-wizard (backend monta prompt -> cria agente ->
 * ativa versão) e, ao concluir, mostra o teste inline da IA (reuso de TestChatTab)
 * com a mensagem "Pronto! Sua IA foi criada...".
 */

import React, { useState, useContext } from "react";
import { Dialog, IconButton, TextField, makeStyles } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { X, Bot, Sparkles, Loader2 } from "lucide-react";
import { toast } from "react-toastify";

import { i18n } from "../../../translate/i18n";
import api from "../../../services/api";
import toastError from "../../../errors/toastError";
import { createModalDesign } from "../../../styles/modalDesign";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { useOnboarding } from "../../../context/Onboarding/OnboardingContext";
import TestChatTab from "../../AiAgentModal/TestChatTab";

const useStyles = makeStyles(theme => {
  const md = createModalDesign(theme);
  const dark = theme.palette.type === "dark";
  return {
    dialog: { ...md.mdDialog, "& .MuiDialog-paper": { ...md.mdDialog["& .MuiDialog-paper"], width: "min(640px, calc(100vw - 32px))" } },
    header: md.mdHeader,
    headerLeft: { display: "flex", alignItems: "center", gap: 12 },
    headerIcon: {
      width: 40, height: 40, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)", color: "#fff"
    },
    title: md.mdTitle,
    subtitle: { fontSize: "0.82rem", color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", marginTop: 2 },
    closeBtn: md.mdCloseBtn,
    content: md.mdContent,
    label: md.mdFieldLabel,
    field: md.mdField,
    actions: md.mdActions,
    // Painel do dropdown (Autocomplete) — moderno, no tom do projeto.
    acPaper: {
      background: dark ? "#16161a" : "#ffffff",
      border: dark ? "1px solid rgba(91,124,255,0.22)" : "1px solid rgba(0,0,0,0.08)",
      borderRadius: 12,
      boxShadow: dark ? "0 16px 40px rgba(0,0,0,0.5)" : "0 12px 32px rgba(0,0,0,0.12)",
      marginTop: 6,
      overflow: "hidden"
    },
    acListbox: {
      padding: 6,
      "&::-webkit-scrollbar": { width: 6 },
      "&::-webkit-scrollbar-thumb": { background: "rgba(91,124,255,0.3)", borderRadius: 3 }
    },
    acOption: {
      fontSize: "0.9rem",
      borderRadius: 8,
      padding: "9px 12px",
      color: dark ? "#e2e8f0" : "#1e293b",
      '&[data-focus="true"]': { background: dark ? "rgba(91,124,255,0.14)" : "rgba(61,92,253,0.07)" },
      '&[aria-selected="true"]': { background: dark ? "rgba(91,124,255,0.2)" : "rgba(61,92,253,0.12)" }
    },
    btnPrimary: md.mdBtnPrimary,
    btnGhost: md.mdBtnGhost,
    successBanner: {
      display: "flex", alignItems: "center", gap: 12, padding: theme.spacing(2),
      borderRadius: 14, marginBottom: theme.spacing(2),
      background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)",
      color: dark ? "#bbf7d0" : "#166534", fontSize: "0.92rem", fontWeight: 600
    },
    fallbackNote: {
      padding: theme.spacing(1.25, 1.5), borderRadius: 10, marginBottom: theme.spacing(1.5),
      background: "rgba(234,179,8,0.1)", border: "1px solid rgba(234,179,8,0.3)",
      color: dark ? "#fde68a" : "#92400e", fontSize: "0.82rem"
    },
    spin: { animation: "$spin 1s linear infinite" },
    "@keyframes spin": { from: { transform: "rotate(0deg)" }, to: { transform: "rotate(360deg)" } }
  };
});

// Segmentos comuns no Brasil — sugestões de autocomplete; o campo ACEITA texto
// livre (combobox), preservando a especificidade que deixa o prompt da IA bom.
const SEGMENT_SUGGESTIONS = [
  "Clínica odontológica", "Clínica médica", "Clínica de estética", "Psicologia / Terapia",
  "Salão de beleza", "Barbearia", "Pet shop", "Clínica veterinária", "Academia / Studio fitness",
  "Escritório de advocacia", "Escritório de contabilidade", "Imobiliária", "Corretora de seguros",
  "Loja de roupas / Moda", "E-commerce", "Restaurante", "Pizzaria / Delivery", "Padaria",
  "Supermercado / Mercado", "Oficina mecânica", "Concessionária", "Escola / Curso de idiomas",
  "Consultoria", "Agência de marketing", "Construtora / Reforma", "Farmácia", "Ótica",
  "Agência de viagens", "Assistência técnica"
];

// Tons curados. O texto escolhido alimenta o prompt E é mapeado a responseStyle por
// regex no backend (mapTomToResponseStyle) — os rótulos foram pensados p/ casar:
// "objetivo" → concise · "consultivo/detalhado" → detailed · demais → balanced.
const TONE_OPTIONS = [
  "Acolhedor e próximo",
  "Profissional e objetivo",
  "Consultivo e detalhado",
  "Formal e institucional",
  "Descontraído e amigável"
];

const FIELD_DEFS = [
  { key: "nomeIA", required: true, multiline: false },
  { key: "segmento", required: true, multiline: false, type: "combo", options: SEGMENT_SUGGESTIONS },
  { key: "tom", required: false, multiline: false, type: "combo", options: TONE_OPTIONS },
  { key: "produtosServicos", required: false, multiline: true },
  { key: "duvidasFrequentes", required: false, multiline: true },
  { key: "regrasAtendimento", required: false, multiline: true },
  { key: "infosImportantes", required: false, multiline: true },
  { key: "situacoesTransferencia", required: false, multiline: true }
];

const EMPTY = FIELD_DEFS.reduce((acc, f) => ({ ...acc, [f.key]: "" }), {});

const AhaWizard = ({ open, onClose }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { refetch } = useOnboarding();

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [agentId, setAgentId] = useState(null);
  const [usedFallback, setUsedFallback] = useState(false);

  const created = !!agentId;

  const reset = () => {
    setForm(EMPTY);
    setAgentId(null);
    setUsedFallback(false);
    setLoading(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleChange = key => e => setForm(prev => ({ ...prev, [key]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.nomeIA.trim() || !form.segmento.trim()) {
      toast.warning(i18n.t("onboarding.aha.requiredWarning"));
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/onboarding/ai-wizard", {
        ...form,
        // Por padrão a IA escala para o admin que criou a conta; o cliente
        // refina o destino depois em /fluxo. Garante a regra de transferência.
        escalationUserId: user?.id
      });
      setAgentId(data.agentId);
      setUsedFallback(!!data.usedFallback);
      refetch();
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open onClose={handleClose} className={classes.dialog} maxWidth={false}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>{created ? <Sparkles size={20} /> : <Bot size={20} />}</div>
          <div>
            <div className={classes.title}>
              {created ? i18n.t("onboarding.aha.successTitle") : i18n.t("onboarding.aha.title")}
            </div>
            <div className={classes.subtitle}>
              {created ? i18n.t("onboarding.aha.successSubtitle") : i18n.t("onboarding.aha.subtitle")}
            </div>
          </div>
        </div>
        <IconButton size="small" className={classes.closeBtn} onClick={handleClose}>
          <X size={18} />
        </IconButton>
      </div>

      <div className={classes.content}>
        {!created && (
          <>
            {FIELD_DEFS.map(f => (
              <div key={f.key}>
                <label className={classes.label}>
                  {i18n.t(`onboarding.aha.fields.${f.key}.label`)}
                  {f.required ? " *" : ""}
                </label>

                {f.type === "combo" ? (
                  // Combobox estilizado: escolhe uma sugestão OU digita o próprio.
                  <Autocomplete
                    freeSolo
                    fullWidth
                    options={f.options}
                    inputValue={form[f.key]}
                    onInputChange={(_, v) => setForm(prev => ({ ...prev, [f.key]: v }))}
                    classes={{ paper: classes.acPaper, listbox: classes.acListbox, option: classes.acOption }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        className={classes.field}
                        variant="outlined"
                        fullWidth
                        placeholder={i18n.t(`onboarding.aha.fields.${f.key}.placeholder`)}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    className={classes.field}
                    variant="outlined"
                    fullWidth
                    multiline={f.multiline}
                    minRows={f.multiline ? 2 : undefined}
                    placeholder={i18n.t(`onboarding.aha.fields.${f.key}.placeholder`)}
                    value={form[f.key]}
                    onChange={handleChange(f.key)}
                  />
                )}
              </div>
            ))}
          </>
        )}

        {created && (
          <>
            <div className={classes.successBanner}>
              <Sparkles size={20} />
              {i18n.t("onboarding.aha.successMessage")}
            </div>
            {usedFallback && (
              <div className={classes.fallbackNote}>{i18n.t("onboarding.aha.fallbackNote")}</div>
            )}
            <TestChatTab agentId={agentId} agentName={form.nomeIA} />
          </>
        )}
      </div>

      <div className={classes.actions}>
        {!created ? (
          <>
            <button type="button" className={classes.btnGhost} onClick={handleClose} disabled={loading}>
              {i18n.t("onboarding.aha.cancel")}
            </button>
            <button type="button" className={classes.btnPrimary} onClick={handleSubmit} disabled={loading}>
              {loading ? <Loader2 size={16} className={classes.spin} /> : <Sparkles size={16} />}
              {i18n.t("onboarding.aha.create")}
            </button>
          </>
        ) : (
          <button type="button" className={classes.btnPrimary} onClick={handleClose}>
            {i18n.t("onboarding.aha.done")}
          </button>
        )}
      </div>
    </Dialog>
  );
};

export default AhaWizard;
